// Shown at the bottom of 會員專區 instead of the on-line application form while
// membership is by invitation only (後台 → 網站設定 → 會員制度與會費 →
// 「開放線上入會申請」關閉，which is the default).

import { Link } from "react-router-dom";
import Icon from "@/components/feature/Icon";
import { inviteProcess } from "@/mocks/membershipData";
import { useSiteSettings } from "@/lib/siteSettings";
import MembershipFaq from "./MembershipFaq";

const MAIL_SUBJECT = "【TAIAA】入會洽詢";

const MAIL_CHECKLIST = ["姓名、服務單位與職稱", "專業領域或產業類別", "有興趣的會員類型（個人／團體／企業）", "希望與協會合作或交流的方向"];

export default function MembershipInvite() {
  const { settings } = useSiteSettings();
  const email = settings.contact.email;
  const mailto = `mailto:${email}?subject=${encodeURIComponent(MAIL_SUBJECT)}`;

  return (
    <section id="join" className="scroll-mt-20 py-24 md:py-32 bg-white" data-testid="membership-invite">
      <div className="px-6 md:px-10 lg:px-16">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
            <p className="text-sm font-medium tracking-[0.2em] text-navy-700 uppercase mb-3">How to Join</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold text-navy-950 leading-tight mb-5">入會方式</h2>
            <p className="text-silver-500 text-base md:text-lg leading-relaxed">
              TAIAA 會員目前採<strong className="text-navy-950 font-semibold">邀請制</strong>
              ：由理監事或現有會員推薦，或由秘書處主動邀請認同協會宗旨、在 AI 應用領域具備實務或研究經驗的個人與單位加入。
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
            {/* Left - Process + FAQ */}
            <div className="lg:col-span-2">
              <h3 className="text-lg font-semibold text-navy-950 mb-8">入會流程</h3>
              <div className="space-y-8">
                {inviteProcess.map((step, idx) => (
                  <div key={step.step} className="flex gap-5">
                    <div className="flex flex-col items-center">
                      <div className="w-10 h-10 rounded-full bg-navy-950 text-white flex items-center justify-center text-sm font-semibold shrink-0">
                        {step.step}
                      </div>
                      {idx < inviteProcess.length - 1 && <div className="w-px flex-1 bg-silver-200 my-2" />}
                    </div>
                    <div className="pb-2">
                      <h4 className="text-base font-semibold text-navy-950 mb-1">{step.title}</h4>
                      <p className="text-sm text-silver-500 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <MembershipFaq className="mt-14" />
            </div>

            {/* Right - Contact card */}
            <div className="lg:col-span-3">
              <div className="relative overflow-hidden bg-navy-950 rounded-3xl p-7 md:p-10 text-white">
                <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/[0.04] blur-3xl" />
                <div className="relative">
                  <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center mb-6">
                    <Icon className="ri-vip-crown-line text-xl text-white" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-semibold mb-4">對加入 TAIAA 有興趣？</h3>
                  <p className="text-silver-300 text-sm md:text-base leading-relaxed mb-6">
                    歡迎來信告訴我們您的背景、專業領域與期望，秘書處會主動與您聯繫，說明適合的會員類型與後續流程。
                  </p>

                  <p className="text-xs font-medium tracking-[0.2em] uppercase text-silver-400 mb-3">來信時請附上</p>
                  <ul className="space-y-2.5 mb-8">
                    {MAIL_CHECKLIST.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm text-silver-300">
                        <Icon className="ri-check-line mt-0.5 text-white shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="flex flex-wrap gap-4">
                    <a
                      href={mailto}
                      className="inline-flex items-center gap-2 bg-white text-navy-950 px-7 py-3.5 rounded-full text-sm font-medium hover:bg-silver-100 transition-colors whitespace-nowrap"
                    >
                      <Icon className="ri-mail-send-line" />
                      來信洽詢入會
                    </a>
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-2 border border-white/30 text-white px-7 py-3.5 rounded-full text-sm font-medium hover:bg-white/10 transition-colors whitespace-nowrap"
                    >
                      聯絡我們
                      <Icon className="ri-arrow-right-line" />
                    </Link>
                  </div>
                  <p className="mt-6 text-xs text-silver-400">
                    秘書處信箱：
                    <a href={mailto} className="underline underline-offset-2 hover:text-white">
                      {email}
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
